import { useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, Send, Rss, Mail, Loader2, CheckCircle, ArrowRight, MapPin, Clock } from "lucide-react";
import axios from "axios";
import { BACKEND_URL, API } from "../lib/api";
import { useLang } from "../context/LangContext";

const categories = [
  { name: "Ekonomi", key: "ekonomi", href: "/?category=Ekonomi" },
  { name: "Gündem", key: "gundem", href: "/?category=Gündem" },
  { name: "Teknoloji", key: "teknoloji", href: "/?category=Teknoloji" },
  { name: "Spor", key: "spor", href: "/?category=Spor" },
  { name: "Dünya", key: "dunya", href: "/?category=Dünya" },
  { name: "Sağlık", key: "saglik", href: "/?category=Sağlık" },
];

const corporateLinks = [
  { name: "Hakkımızda", key: "about", href: "/hakkimizda" },
  { name: "Künye", key: "masthead", href: "/kunye" },
  { name: "İletişim", key: "contact", href: "/iletisim" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const { t } = useLang();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() });
      setStatus("success");
      setMessage(res.data.message);
      setEmail("");
      setTimeout(() => setStatus(null), 4000);
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Bir hata olustu");
      setTimeout(() => setStatus(null), 4000);
    }
  };

  return (
    <footer className="relative bg-[#0a0a0b] overflow-hidden" data-testid="footer">
      {/* Ambient Glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.07] hidden lg:block" style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)" }} />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-[0.04] hidden lg:block" style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)" }} />

      {/* Watermark */}
      <div className="absolute top-8 left-0 right-0 hidden lg:flex justify-center pointer-events-none select-none overflow-hidden">
        <span className="text-[12rem] md:text-[18rem] font-black tracking-tighter text-white opacity-[0.03] leading-none">LOFT</span>
      </div>

      <div className="relative z-10">
        {/* Mobile Footer */}
        <div className="lg:hidden px-4 pt-10 pb-6">
          {/* Logo & Description */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-400 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="18" rx="1.5" />
                <rect x="8" y="3" width="13" height="18" rx="1.5" />
              </svg>
            </div>
            <div>
              <span className="text-lg font-bold text-white">loft<span className="text-amber-500">.press</span></span>
              <p className="text-[10px] text-zinc-500 tracking-[0.15em] uppercase">Bağımsız Haber</p>
            </div>
          </div>
          <p className="text-zinc-500 text-sm leading-relaxed mb-6">{t('footerDesc')}</p>

          {/* Newsletter Mobile */}
          <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-amber-500" />
              <h4 className="text-sm font-bold text-white">{t('newsletterTitle')}</h4>
            </div>
            <p className="text-zinc-400 text-xs mb-3">{t('newsletterDesc')}</p>
            <form onSubmit={handleSubscribe} className="flex gap-2" data-testid="newsletter-form-mobile">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('emailPlaceholder')}
                className="flex-1 px-3 py-2.5 bg-zinc-950 border border-zinc-700/50 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-amber-500"
                required
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm rounded-xl disabled:opacity-50 flex items-center justify-center"
              >
                {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
            {status && status !== "loading" && (
              <p className={`mt-2 text-xs ${status === "success" ? "text-emerald-400" : "text-red-400"}`}>{message}</p>
            )}
          </div>

          {/* Quick Links Mobile */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">{t('categories_title')}</h4>
              <ul className="space-y-2">
                {categories.slice(0, 4).map((cat) => (
                  <li key={cat.name}>
                    <Link to={cat.href} className="text-zinc-400 text-sm">{t(cat.key)}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">{t('corporate')}</h4>
              <ul className="space-y-2">
                {corporateLinks.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-zinc-400 text-sm">{t(item.key)}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Social Links Mobile */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <a href="https://t.me/loftpress" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-zinc-800/80 border border-zinc-700/50 rounded-lg flex items-center justify-center">
              <Send className="w-4 h-4 text-zinc-400" />
            </a>
            <a href={`${BACKEND_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-zinc-800/80 border border-zinc-700/50 rounded-lg flex items-center justify-center">
              <Rss className="w-4 h-4 text-zinc-400" />
            </a>
          </div>

          {/* Copyright Mobile */}
          <div className="text-center pt-4 border-t border-zinc-800/60">
            <p className="text-[10px] text-zinc-600">&copy; {new Date().getFullYear()} Loft Press. {t('copyright')}</p>
          </div>
        </div>

        {/* Desktop Footer - Original */}
        <div className="hidden lg:block max-w-7xl mx-auto px-6 pt-20 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">

            {/* Brand Column */}
            <div className="md:col-span-4">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-400 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="18" rx="1.5" />
                    <rect x="8" y="3" width="13" height="18" rx="1.5" />
                    <line x1="11" y1="7" x2="18" y2="7" />
                    <line x1="11" y1="10.5" x2="17" y2="10.5" />
                    <line x1="11" y1="14" x2="16" y2="14" />
                  </svg>
                </div>
                <div>
                  <span className="text-xl font-bold text-white">loft<span className="text-amber-500">.press</span></span>
                  <p className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase">Bağımsız Haber</p>
                </div>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6 max-w-xs">
                {t('footerDesc')}
              </p>
              <div className="flex flex-col gap-2.5 text-sm text-zinc-500">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-zinc-600" />
                  <span>{t('location')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-zinc-600" />
                  <span>{t('alwaysOn')}</span>
                </div>
              </div>
            </div>

            {/* Category Links */}
            <div className="md:col-span-2">
              <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-5">{t('categories_title')}</h4>
              <ul className="space-y-3">
                {categories.map((cat) => (
                  <li key={cat.name}>
                    <Link to={cat.href} className="text-zinc-400 hover:text-white hover:translate-x-1 inline-flex items-center gap-1.5 transition-all duration-200 text-sm group">
                      <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200 text-amber-500" />
                      {t(cat.key)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Corporate Links */}
            <div className="md:col-span-2">
              <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-5">{t('corporate')}</h4>
              <ul className="space-y-3">
                {corporateLinks.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-zinc-400 hover:text-white hover:translate-x-1 inline-flex items-center gap-1.5 transition-all duration-200 text-sm group">
                      <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200 text-amber-500" />
                      {t(item.key)}
                    </Link>
                  </li>
                ))}
              </ul>
              <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-4 mt-8">{t('followUs')}</h4>
              <div className="flex items-center gap-3">
                <a href="https://t.me/loftpress" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-zinc-800/80 hover:bg-blue-500/20 border border-zinc-700/50 hover:border-blue-500/50 rounded-lg flex items-center justify-center transition-all duration-200 group" title="Telegram">
                  <Send className="w-4 h-4 text-zinc-400 group-hover:text-blue-400 transition-colors duration-200" />
                </a>
                <a href={`${BACKEND_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-zinc-800/80 hover:bg-orange-500/20 border border-zinc-700/50 hover:border-orange-500/50 rounded-lg flex items-center justify-center transition-all duration-200 group" title="RSS Feed" data-testid="footer-rss-link">
                  <Rss className="w-4 h-4 text-zinc-400 group-hover:text-orange-400 transition-colors duration-200" />
                </a>
              </div>
            </div>

            {/* Newsletter Card */}
            <div className="md:col-span-4">
              <div className="relative bg-zinc-900/60 backdrop-blur-sm border border-zinc-800/80 rounded-2xl p-6 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)" }} />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-5 h-5 text-amber-500" />
                    <h4 className="text-base font-bold text-white">{t('newsletterTitle')}</h4>
                  </div>
                  <p className="text-zinc-400 text-sm mb-5">{t('newsletterDesc')}</p>
                  <form onSubmit={handleSubscribe} className="space-y-3" data-testid="newsletter-form">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('emailPlaceholder')}
                      className="w-full px-4 py-3 bg-zinc-950 border border-zinc-700/50 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 transition-all duration-200"
                      required
                      data-testid="newsletter-email-input"
                    />
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm rounded-xl transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="newsletter-subscribe-btn"
                    >
                      {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : status === "success" ? <CheckCircle className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                      {status === "success" ? t('subscribed') : t('subscribe')}
                    </button>
                  </form>
                  {status && status !== "loading" && (
                    <p className={`mt-3 text-xs ${status === "success" ? "text-emerald-400" : "text-red-400"}`} data-testid="newsletter-message">{message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar - Desktop Only */}
        <div className="hidden lg:block border-t border-zinc-800/60">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <p className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} Loft Press. {t('copyright')}</p>
              <div className="flex items-center gap-1 text-xs text-zinc-600">
                <span>{t('footerSlogan')}</span>
                <span className="text-amber-500/60 mx-1">|</span>
                <span>loft.press</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
